import { Trans, useTranslation } from '@exam-room/translations';
import { cn } from '@exam-room/utils';

import { Chip, WatchLaterOutlinedIcon } from '../../../../components';
import { type QConfigStructure } from '../../../exam/instruction/types';
import { getStatusLabel } from '../../../exam/instruction/utils';
import { formatDurationToTimeObject } from '../../../exam/instruction/utils/format-time';

export interface ExamCourseProps {
  title: string;
  startDate: Date;
  endDate: Date;
  courses: QConfigStructure[];
  status: 'preparing' | 'waiting' | 'completed';
}

export default function ExamCourse(props: ExamCourseProps) {
  const { title, startDate, endDate, courses, status } = props;

  const { t } = useTranslation();

  const totalQuestionCount = courses.reduce((acc, course) => acc + course.totalQuestions, 0);
  const duration = endDate.getTime() - startDate.getTime();

  const timeObject = formatDurationToTimeObject(duration);

  return (
    <div
      className={cn(
        'text-text-primary flex flex-col gap-4 rounded-md border border-gray-300 p-4',
        status === 'completed' && 'bg-gray-50',
      )}
    >
      <div className='flex w-full flex-col gap-2'>
        <div className='flex flex-row justify-between'>
          <h5>{title}</h5>
          <Chip
            label={t(getStatusLabel(status))}
            variant={status === 'preparing' ? 'warning' : 'gray'}
            size='lg'
          />
        </div>
        <div className='text-text-secondary flex justify-between'>
          <div className='flex flex-row gap-2'>
            <div className='flex flex-row items-center gap-2'>
              <WatchLaterOutlinedIcon className='text-xl' />
              <p>
                <Trans
                  i18nKey='instruction.examDetails.exam.duration'
                  values={{
                    hours: timeObject.hours,
                    minutes: timeObject.minutes,
                  }}
                />
              </p>
            </div>
          </div>
          <div className='flex gap-1'>
            <p>{t('instruction.examDetails.numberOfTotalQuestion')}</p>
            <p className='font-semibold'>&nbsp;{totalQuestionCount}</p>
            <p>{t('instruction.examDetails.questionUnit')}</p>
          </div>
        </div>
      </div>
      <div
        className={cn(
          'flex flex-col gap-4 rounded-md p-4',
          status === 'preparing' ? 'bg-warning-100' : 'bg-gray-50',
        )}
      >
        {courses.map(({ id, name, totalQuestions, passingCriteria }) => (
          <div className='flex justify-between' key={id}>
            <div className='flex flex-row gap-2'>
              <p>{name}</p>
              {passingCriteria ? (
                <p className='text-danger-500'>({t('instruction.examDetails.mustPassTopic')})</p>
              ) : null}
            </div>
            <div className='flex gap-1'>
              <p>{t('instruction.examDetails.numberOfQuestion')}</p>
              <p className='font-semibold'>&nbsp;{totalQuestions}</p>
              <p>{t('instruction.examDetails.questionUnit')}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
